import type React from "react";
import type { McqModel } from "../../../lib/stageViewModels";
import { useGrowIn } from "./useGrowIn";

/** Ranked horizontal bars, percentages of respondents. */
export function McqBars({ model }: { model: McqModel }) {
  const { grown, reduced, delay } = useGrowIn();
  if (model.base === 0) return <Empty />;
  // Compact above five options so up to eight rows fit a 1080p projector;
  // beyond that the list scrolls instead of overflowing into the base line.
  const compact = model.rows.length > COMPACT_FROM;
  return (
    <div className="flex flex-col h-full text-rc-paper">
      <BaseLine label={model.baseLabel} />
      <ScrollCenter as="ul" className={compact ? "gap-2.5" : "gap-4"}>
        {model.rows.map((row, i) => (
          <li key={row.key} className={`flex flex-col ${compact ? "gap-1" : "gap-2"}`}>
            <div className="flex items-baseline justify-between gap-6">
              <span
                className="font-semibold leading-tight text-balance"
                style={{ fontSize: compact ? "clamp(0.9rem, 1.2vw, 1.2rem)" : "clamp(1rem, 1.6vw, 1.6rem)" }}
              >
                {row.label}
              </span>
              <span className="flex items-baseline gap-2 flex-shrink-0 tabular-nums">
                <span
                  className="font-bold tracking-[-0.02em]"
                  style={{ fontSize: compact ? "clamp(1.1rem, 1.7vw, 1.8rem)" : "clamp(1.4rem, 2.4vw, 2.6rem)" }}
                >
                  {row.pct}%
                </span>
                <span className="text-[13px] text-rc-paper/50">{row.count}</span>
              </span>
            </div>
            <div className={`${compact ? "h-2.5" : "h-4"} rounded-full bg-rc-paper/[0.08] overflow-hidden`}>
              <div
                className={`h-full rounded-full ${row.isOther ? "bg-rc-paper/35" : i === 0 ? "bg-rc-lime" : "bg-rc-berry"} ${reduced ? "" : "transition-[width] duration-700 ease-rc-out"}`}
                style={{
                  width: grown ? `${row.pct}%` : "0%",
                  transitionDelay: delay(i),
                }}
                aria-label={`${row.label}: ${row.count} (${row.pct} Prozent)`}
              />
            </div>
          </li>
        ))}
      </ScrollCenter>
    </div>
  );
}

/** Row count above which the choice views switch to their compact density. */
export const COMPACT_FROM = 5;

/**
 * Fills the remaining height, centres its children when they fit and
 * scrolls (thin scrollbar, mouse wheel / arrow keys) when they do not —
 * `my-auto` on the inner list is what makes "centre or scroll" one layout.
 */
export function ScrollCenter({
  as: Tag = "div",
  className = "",
  children,
}: {
  as?: "div" | "ul";
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent]">
      <Tag className={`my-auto pb-1 flex flex-col ${className}`}>{children}</Tag>
    </div>
  );
}

export function BaseLine({ label }: { label: string }) {
  return (
    <div className="flex-shrink-0 mb-5 text-[11px] font-bold uppercase tracking-[0.28em] text-rc-paper/50 tabular-nums">
      {label}
    </div>
  );
}

export function Empty() {
  return (
    <div className="h-full flex items-center justify-center">
      <p className="text-[15px] text-rc-paper/45">Antworten erscheinen hier live.</p>
    </div>
  );
}
